/**
 * TradingView Datafeed for Tomi-no-Kami Dashboard
 * Connects to Coinbase public API for real-time BTC futures charting.
 * Adapted from hologami/stratagem_nexus for REDEX.
 */

interface Bar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

const symbolMap: Record<string, string> = {
  'COINBASE:BTCUSD': 'BTC-USD',
  'COINBASE:ETHUSD': 'ETH-USD',
  'COINBASE:SOLUSD': 'SOL-USD',
  'COINBASE:XRPUSD': 'XRP-USD',
  'COINBASE:DOGEUSD': 'DOGE-USD',
};

export function createCoinbaseDatafeed() {
  const subscribers = new Map<string, { interval?: ReturnType<typeof setInterval> }>();

  return {
    onReady: (callback: (config: unknown) => void) => {
      setTimeout(() => {
        callback({
          supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D', 'W'],
          exchanges: [{ value: 'COINBASE', name: 'Coinbase', desc: 'Coinbase' }],
          symbols_types: [{ name: 'crypto', value: 'crypto' }],
        });
      }, 0);
    },

    searchSymbols: (
      _userInput: string,
      _exchange: string,
      _symbolType: string,
      onResult: (result: unknown[]) => void,
    ) => {
      onResult(
        Object.keys(symbolMap).map((s) => ({
          symbol: s,
          full_name: s,
          description: s.replace('COINBASE:', ''),
          exchange: 'COINBASE',
          type: 'crypto',
        })),
      );
    },

    resolveSymbol: (
      symbolName: string,
      onResolved: (info: unknown) => void,
      onError: (reason: string) => void,
    ) => {
      const productId = symbolMap[symbolName];
      if (!productId) {
        onError(`Unknown symbol: ${symbolName}`);
        return;
      }
      setTimeout(
        () =>
          onResolved({
            name: symbolName,
            description: symbolName.replace('COINBASE:', ''),
            type: 'crypto',
            session: '24x7',
            timezone: 'Etc/UTC',
            exchange: 'COINBASE',
            minmov: 1,
            pricescale: 100,
            has_intraday: true,
            has_daily: true,
            has_weekly_and_monthly: true,
            supported_resolutions: ['1', '5', '15', '30', '60', '240', 'D', 'W'],
            volume_precision: 8,
            data_status: 'streaming',
          }),
        0,
      );
    },

    getBars: async (
      symbolInfo: { name: string },
      resolution: string,
      periodParams: { from: number; to: number },
      onHistory: (bars: Bar[], meta: { noData: boolean }) => void,
    ) => {
      try {
        const productId = symbolMap[symbolInfo.name];
        if (!productId) {
          onHistory([], { noData: true });
          return;
        }

        const granularity = getGranularity(resolution);
        const maxCandles = 200;
        const rangeSeconds = maxCandles * granularity;
        const toTime = periodParams.to;
        const fromTime =
          periodParams.to - periodParams.from > rangeSeconds
            ? toTime - rangeSeconds
            : periodParams.from;

        const url = `https://api.exchange.coinbase.com/products/${productId}/candles?granularity=${granularity}&start=${fromTime}&end=${toTime}`;

        const res = await fetch(url);
        if (!res.ok) {
          if (res.status === 503) {
            await new Promise((r) => setTimeout(r, 2000));
            const retry = await fetch(url);
            if (!retry.ok) {
              onHistory([], { noData: true });
              return;
            }
            const d = await retry.json();
            onHistory(d?.length ? formatBars(d) : [], { noData: !d?.length });
            return;
          }
          onHistory([], { noData: true });
          return;
        }

        const data = await res.json();
        if (!data?.length) {
          onHistory([], { noData: true });
          return;
        }
        onHistory(formatBars(data), { noData: false });
      } catch {
        onHistory([], { noData: true });
      }
    },

    subscribeBars: (
      symbolInfo: { name: string },
      _resolution: string,
      onRealtime: (bar: Bar) => void,
      subscriberUID: string,
    ) => {
      const productId = symbolMap[symbolInfo.name];
      if (!productId) return;

      const interval = setInterval(async () => {
        try {
          const r = await fetch(
            `https://api.exchange.coinbase.com/products/${productId}/ticker`,
          );
          if (!r.ok) return;
          const t = await r.json();
          const price = parseFloat(t.price);
          onRealtime({
            time: new Date(t.time).getTime(),
            open: price,
            high: price,
            low: price,
            close: price,
            volume: parseFloat(t.volume || '0'),
          });
        } catch {
          /* silent */
        }
      }, 5000);

      subscribers.set(subscriberUID, { interval });
    },

    unsubscribeBars: (uid: string) => {
      const sub = subscribers.get(uid);
      if (sub?.interval) clearInterval(sub.interval);
      subscribers.delete(uid);
    },
  };
}

function getGranularity(resolution: string): number {
  const map: Record<string, number> = {
    '1': 60,
    '5': 300,
    '15': 900,
    '30': 900,
    '60': 3600,
    '240': 21600,
    D: 86400,
    W: 86400,
  };
  return map[resolution] || 3600;
}

function formatBars(data: number[][]): Bar[] {
  // Coinbase: [time, low, high, open, close, volume]
  return data
    .map((c) => ({
      time: c[0] * 1000,
      low: c[1],
      high: c[2],
      open: c[3],
      close: c[4],
      volume: c[5],
    }))
    .sort((a, b) => a.time - b.time);
}
